import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { CasaTemaContext, casaTema, PASO, type CasaTema } from '@components/M02/casaTema';
import { Button } from './Button';

interface Props { children: ReactNode; fallback?: ReactNode; /** «Avisar al equipo». Sin esto se manda el error a Sentry marcado como avisado por la persona. */ onAvisar?: () => void }
interface State { hasError: boolean; message: string }

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Importing a module script failed') ||
    error.message?.includes('Unable to preload CSS') ||
    error.message?.includes('error loading dynamically imported module')
  );
}

export class ErrorBoundary extends Component<Props, State> {
  static contextType = CasaTemaContext;
  declare context: React.ContextType<typeof CasaTemaContext>;
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): State {
    // Chunk hash mismatch after deploy → reload silently to get fresh index.html
    if (isChunkLoadError(error)) {
      window.location.reload();
      return { hasError: false, message: '' };
    }
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!isChunkLoadError(error)) {
      // I-229: UN solo ErrorBoundary — antes el de la raíz (`components/ErrorBoundary.tsx`) reportaba a Sentry y éste no.
      Sentry.captureException(error);
      console.error('[ErrorBoundary]', error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      const T = this.context ?? casaTema(false);
      return this.props.fallback ?? (
        <RedDeError T={T} onReintentar={() => this.setState({ hasError: false, message: '' })}
          onAvisar={this.props.onAvisar ?? (() => Sentry.captureException(new Error(this.state.message), { tags: { avisado_por_usuario: 'si' } }))} />
      );
    }
    return this.props.children;
  }
}

/** `casa-piezas-estructura.jsx:168-184` (`RedDeError`, catálogo del 5-sep; el vestido del 12-sep no la redibuja): tarjeta `dangerSoft`/`dangerLine`,
 *  radio 14, relleno 26/22, icono 40 radio 12, título 15/700, texto 12.5 `fg2` ancho 380, botones con gap 9. Sin hex. */
const RedDeError: React.FC<{ T: CasaTema; onReintentar: () => void; onAvisar: () => void }> = ({ T, onReintentar, onAvisar }) => {
  const [avisado, setAvisado] = useState(false);
  return (
    <div role="alert" style={{ padding: '26px 22px', borderRadius: 14, border: `1px solid ${T.dangerLine}`, background: T.dangerSoft, textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: T.dangerSoft, border: `1px solid ${T.dangerLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <AlertTriangle size={18} color={T.danger} />
      </div>
      <div style={{ fontSize: PASO.s15, fontWeight: 700, color: T.fg }}>Esta parte se cayó</div>
      <div style={{ fontSize: PASO.s12_5, color: T.fg2, maxWidth: 380, margin: '6px auto 0', lineHeight: 1.5 }}>
        No perdiste nada de lo que estabas viendo en el resto de la pantalla. Si acabamos de subir una versión nueva, esto se recarga solo.
      </div>
      <div style={{ display: 'flex', gap: 9, justifyContent: 'center', marginTop: 15 }}>
        <Button tema={T} variant="secondary" onClick={onReintentar}>Reintentar</Button>
        <Button tema={T} variant="ghost" disabled={avisado} onClick={() => { onAvisar(); setAvisado(true); }}>
          {avisado ? 'Ya avisamos al equipo' : 'Avisar al equipo'}
        </Button>
      </div>
    </div>
  );
};
